import React from 'react';
import { Stack, Group, Text, Paper, Textarea, Button, SegmentedControl, Checkbox, Divider } from '@mantine/core';
import { ResponsiveDrawer, useIsBottomSheet } from '../UI/ResponsiveDrawer';
import { IconRefresh, IconInfoCircle, IconBookmark, IconPencil } from '@tabler/icons-react';
import { DrawerHeader } from '../UI/DrawerHeader';
import { Recipe } from '../../types/mealPlan';
import { getRecipeFeedbackTags } from '../../utils/feedbackTags';
import { selectedIngredientDisplays } from '../../utils/ingredientChips';
import { FeedbackTagChips } from './FeedbackTagChips';

/** "green beans", "green beans and asparagus", "a, b and c" */
const formatList = (items: string[]): string => {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

interface RecipeCustomizationDrawerProps {
  opened: boolean;
  onClose: () => void;
  recipe?: Recipe; // Recipe being swapped; drives the dynamic reason chips
  // Set when the day being swapped holds a penciled-in meal instead of a
  // recipe: hides recipe-anchored controls (reason chips come along for free,
  // recipe is undefined) and seeds the copy with the meal's name
  penciledName?: string;
  selectedDay?: string;
  kids?: number; // household kid count; gates the kids chip
  avoidTerms?: string[]; // already-excluded foods; suppresses redundant ingredient chips
  reasons: string[];
  onReasonsChange: (reasons: string[]) => void;
  alwaysAvoid: boolean; // opt-in to promote tapped ingredients to foods-to-avoid
  onAlwaysAvoidChange: (value: boolean) => void;
  instructions: string;
  onInstructionsChange: (instructions: string) => void;
  modifyExisting: boolean;
  onModifyExistingChange: (modifyExisting: boolean) => void;
  onSubmit: () => void;
  // Opens the cookbook picker instead (quota-free path)
  onChooseFromCookbook: () => void;
  // Opens the pencil-in sheet: replace the day with a typed-in, recipe-less
  // meal. Absent when the day can't take one (a cook day other days eat from:
  // a custom meal has no batch for the references to draw on)
  onPencilIn?: () => void;
  // AI substitutions are capped per plan; cookbook swaps stay available
  quotaExhausted?: boolean;
  loading?: boolean;
}

export const RecipeCustomizationDrawer: React.FC<RecipeCustomizationDrawerProps> = ({
  opened,
  onClose,
  recipe,
  penciledName,
  selectedDay,
  kids = 0,
  avoidTerms,
  reasons,
  onReasonsChange,
  alwaysAvoid,
  onAlwaysAvoidChange,
  instructions,
  onInstructionsChange,
  modifyExisting,
  onModifyExistingChange,
  onSubmit,
  onChooseFromCookbook,
  onPencilIn,
  quotaExhausted = false,
  loading = false
}) => {
  // Chips the modify-mode prompt can't satisfy: it hard-requires keeping the
  // cuisine ("keep the tacos but not Mexican" is unanswerable), and modifying
  // the same dish can't fix having had it recently. Composition chips
  // (protein, vegetarian, format, spice, effort) all have valid modify
  // readings — "this bowl but with chicken", "make it a bowl".
  const REPLACE_ONLY_TAGS = new Set(['dislike_cuisine', 'had_recently']);

  // The swap-in pair shares one row; the full labels clip in half-width slots
  // on the mobile bottom sheet, so they shorten there ("or swap in" carries
  // the sentence either way)
  const isBottomSheet = useIsBottomSheet();

  const allReasonTags = recipe
    ? getRecipeFeedbackTags(recipe, undefined, { context: 'swap', kids, avoidTerms })
    : [];
  const reasonTags = modifyExisting
    ? allReasonTags.filter((t) => !REPLACE_ONLY_TAGS.has(t.id))
    : allReasonTags;

  // Only the chips still on offer count: a stale id left in `reasons` must not
  // put a name in the opt-in label for a chip the user can no longer see.
  const offeredIds = new Set(reasonTags.map((t) => t.id));
  const selectedIngredients = selectedIngredientDisplays(
    reasons.filter((r) => offeredIds.has(r))
  );

  const toggleReason = (tagId: string) => {
    const next = reasons.includes(tagId)
      ? reasons.filter((r) => r !== tagId)
      : [...reasons, tagId];
    onReasonsChange(next);
    // Deselecting the last ingredient chip hides the opt-in, so clear it too
    // rather than leaving a checked box the user can't see.
    if (alwaysAvoid && selectedIngredientDisplays(next).length === 0) {
      onAlwaysAvoidChange(false);
    }
  };

  const handleModeChange = (nextModifyExisting: boolean) => {
    onModifyExistingChange(nextModifyExisting);
    // Prune selections that are no longer offered, so a hidden chip can't
    // ride along in the submit
    if (nextModifyExisting) {
      onReasonsChange(reasons.filter((r) => !REPLACE_ONLY_TAGS.has(r)));
    }
  };

  return (
    <ResponsiveDrawer
      opened={opened}
      onClose={onClose}
      title={
        <DrawerHeader
          icon={<IconRefresh size={18} />}
          title="Swap this meal"
          subtitle={recipe?.name ?? penciledName}
        />
      }
      size="md"
    >
      <Stack gap="lg">
        {/* With the AI swap quota exhausted the form feeds nothing — the only
            live path is the cookbook, so skip straight to it */}
        {!quotaExhausted && (
        <>
        {/* Reason chips: optional, attribute the rejection so the replacement
            (and future plans) can account for it */}
        {reasonTags.length > 0 && (
          <Stack gap="sm">
            <Text fw={500}>What's not working about {selectedDay}'s meal?</Text>
            <Text size="sm" c="dimmed">
              Helps us pick a better replacement and improves future plans.
            </Text>
            <FeedbackTagChips
              tags={reasonTags}
              selected={new Set(reasons)}
              onToggle={toggleReason}
              showGroupLabels={false}
              collapseSecondary
            />
            {/* The chips themselves are moment-scoped ("don't want this
                tonight"). Turning one into a standing preference is a bigger
                claim, so it stays an explicit opt-in rather than something we
                infer from a single tap. */}
            {selectedIngredients.length > 0 && (
              <Checkbox
                checked={alwaysAvoid}
                onChange={(event) =>
                  onAlwaysAvoidChange(event.currentTarget.checked)
                }
                label={`Always avoid ${formatList(selectedIngredients)}`}
                description="Adds to your foods to avoid, so we keep it out of future plans. You can undo this in preferences."
                size="sm"
              />
            )}
          </Stack>
        )}

        {/* Mode Selection. A penciled-in day has no recipe to modify, so the
            toggle hides and the flow is always a replacement */}
        {!penciledName && (
        <Stack gap="xs">
          <Text fw={500}>How should we change it?</Text>
          <SegmentedControl
            value={modifyExisting ? 'modify' : 'replace'}
            onChange={(value) => handleModeChange(value === 'modify')}
            data={[
              {
                label: 'Replace it',
                value: 'replace'
              },
              {
                label: 'Modify this recipe',
                value: 'modify'
              }
            ]}
            color="orange"
            fullWidth
          />
        </Stack>
        )}

        {/* Instructions Section */}
        <Stack gap="sm">
          <Textarea
            label={modifyExisting ? 'What would you like to change?' : 'Anything specific in mind?'}
            description={
              penciledName
                ? `Leave blank and we'll build a recipe from "${penciledName}".`
                : modifyExisting
                ? "We'll keep the same dish and only change what you ask for."
                : "Leave blank and we'll pick for you."
            }
            placeholder={
              modifyExisting
                ? '• Use beef instead of chicken\n• Make it less spicy\n• More protein'
                : '• Something Mexican\n• A pasta dish\n• Higher protein, lower carb'
            }
            value={instructions}
            onChange={(event) => onInstructionsChange(event.currentTarget.value)}
            rows={3}
            resize="none"
            size="sm"
          />
        </Stack>
        </>
        )}

        {quotaExhausted && (
          <Paper p="md" bg="yellow.0" radius="lg">
            <Group gap="xs" align="flex-start">
              <IconInfoCircle size={16} color="var(--mantine-color-yellow-8)" style={{ flexShrink: 0, marginTop: 1 }} />
              <Text size="xs" c="yellow.8" style={{ flex: 1 }}>
                {penciledName
                  ? "You've used all the new-recipe swaps for this plan. You can still swap in a recipe from your cookbook."
                  : "You've used all the new-recipe swaps for this plan. You can still swap in a recipe from your cookbook or pencil in a meal of your own."}
              </Text>
            </Group>
          </Paper>
        )}

        <Stack gap="sm">
          {!quotaExhausted && (
            <Button
              onClick={onSubmit}
              disabled={
                (modifyExisting && !instructions.trim() && reasons.length === 0) ||
                loading
              }
              loading={loading}
              size="md"
              leftSection={<IconRefresh size={16} />}
              fullWidth
            >
              {modifyExisting ? 'Modify this recipe' : 'Find a new recipe'}
            </Button>
          )}
          {/* The quota-free replacement sources share one compact row: the
              cookbook picker and pencil-in (a typed-in, recipe-less meal that
              still feeds the grocery list). Hidden while modifying the current
              dish; in the quota-exhausted state they're the only paths and the
              divider goes too. Dismissal is the drawer's own close affordances
              (X on desktop, swipe/tap-out on mobile), so there's no Cancel. */}
          {(quotaExhausted || !modifyExisting) && (
            <>
              {!quotaExhausted && <Divider label="or swap in" labelPosition="center" />}
              <Group grow>
                <Button
                  variant="default"
                  onClick={onChooseFromCookbook}
                  disabled={loading}
                  size="sm"
                  leftSection={<IconBookmark size={16} />}
                >
                  {isBottomSheet ? 'From cookbook' : 'From my cookbook'}
                </Button>
                {/* Already-penciled days don't offer pencil-in again; editing
                    the meal lives on the card's kebab */}
                {!penciledName && onPencilIn && (
                  <Button
                    variant="default"
                    onClick={onPencilIn}
                    disabled={loading}
                    size="sm"
                    leftSection={<IconPencil size={16} />}
                  >
                    {isBottomSheet ? 'Pencil in' : 'Pencil in a meal'}
                  </Button>
                )}
              </Group>
            </>
          )}
        </Stack>
      </Stack>
    </ResponsiveDrawer>
  );
};
